// UnitGob — the unit/structure behavioral base. game/Unit.cpp:241-1080 +
// game/ht.h:6556-6665 + the range helpers in game/GameObjects.cpp:457-520.
// Host seams (no sim effect): minimap redraw, menu close, gevm redraw flags, the
// gpplrLocal-gated fog reveal (fog is per-client BY DESIGN and unhashed; the fogmap port
// lands at M3). Perf flags are PINNED SIM CONSTANTS (ctx.wfPerfOptions; the MP lobby
// forces kfPerfMax — tasks/sim-core-map.md hotspot).

import type { SimContext } from '../context.ts';
import { Animation, kfAniLoop, kfAniIgnoreFirstAdvance } from '../anim/animation.ts';
import {
  Gob,
  kfGobActive,
  kfGobLayerDepthSorted,
  kfGobLayerSelection,
  kfGobMobileUnit,
  kfGobNoEnemiesNearby,
  kfGobSelected,
  kfGobStateMachine,
  kfGobStructure,
  kfGobUnit,
  kfGobBeingBuilt,
  kfGobFlashing,
  kfGobDrawFlashed,
  kfGobRedraw,
} from './gob.ts';
import {
  UnitConsts,
  UpdFromMsec,
  kfUntcLargeDefog,
  kfUntcNotifyEnemyNearby,
} from './unitconsts.ts';
import { Player } from '../player/player.ts';
import {
  kumInfantry,
  kumVehicles,
  kumStructures,
  kfUnitInvulnerable,
  kfUnitDrawHealthIndicator,
  kfUnitHilighted,
  knIncreasedFirePowerPercent,
  kfPerfEnemyDamageIndicator,
} from '../player/masks.ts';
import { kfHcapIncreasedFirePower } from '../player/side.ts';
import {
  kgidNull,
  kmidEnemyNearby,
  kmidNearbyAllyHit,
  kmidReservedEnter,
  newMessage,
} from '../sm/message.ts';
import { markDeadSpan } from '../sm/stream.ts';
import { TcFromWc, WcFromTc, WcTrunc } from '../math/coords.ts';
import { s16, idiv } from '../math/int32.ts';
import { GetSideMask, TRect } from '../types.ts';
import type { WPoint, TPoint } from '../types.ts';
import { IniScanf } from '../data/ini.ts';

// game/ht.h:6556-6558
export const kcmsDamageIndicatorTimeout = 4000;
export const kcEnemyNearbyRemember = 5;
// game/Unit.cpp:858 + :981
export const ktcSightRangeMax = 5;
export const ktcNotifyAlliesOfHitRange = 4;

// game/GameObjects.cpp:457-468 — round(sqrt(x^2+y^2)) for 0..9 (generated table, verbatim)
export const gmpDistFromDxy: number[][] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [2, 2, 3, 4, 4, 5, 6, 7, 8, 9],
  [3, 3, 4, 4, 5, 6, 7, 8, 9, 9],
  [4, 4, 4, 5, 6, 6, 7, 8, 9, 10],
  [5, 5, 5, 6, 6, 7, 8, 9, 9, 10],
  [6, 6, 6, 7, 7, 8, 8, 9, 10, 11],
  [7, 7, 7, 8, 8, 9, 9, 10, 11, 11],
  [8, 8, 8, 9, 9, 9, 10, 11, 11, 12],
  [9, 9, 9, 9, 10, 10, 11, 11, 12, 13],
];

export class UnitGob extends Gob {
  m_puntc: UnitConsts;
  m_agidEnemyNearby: number[] = new Array(kcEnemyNearbyRemember).fill(kgidNull);
  m_fxHealth = 0;
  m_ani = new Animation();
  m_wfUnit = 0;
  m_cupdLastHitNotify = -1000000;
  m_cDamageCountdown = 0; // short

  // game/Unit.cpp:241-253
  constructor(ctx: SimContext, puntc: UnitConsts) {
    super(ctx);
    this.m_ff = (this.m_ff | kfGobUnit | kfGobStateMachine | kfGobLayerDepthSorted) >>> 0;
    this.m_puntc = puntc;
  }

  override GetType(): number {
    return this.m_puntc.gt;
  } // Unit.cpp:372-375
  GetConsts(): UnitConsts {
    return this.m_puntc;
  } // ht.h:6598
  GetUnitType(): number {
    return this.m_puntc.ut;
  } // ht.h:6602
  GetHealth(): number {
    return this.m_fxHealth;
  } // ht.h:6616
  GetUnitFlags(): number {
    return this.m_wfUnit;
  } // ht.h:6606
  SetUnitFlags(wf: number): void {
    this.m_wfUnit = wf & 0xffff;
  } // ht.h:6610

  // game/Unit.cpp:261-274 — level-file init: "%*d ,%d ,%d ,%d ,%d ,%d"
  // (type skipped, side, tx, ty, flags, health%); health = (armor * n) / 100 in LONG math.
  InitFromArgs(value: string): boolean {
    const { count, values } = IniScanf(value, '%*d ,%d ,%d ,%d ,%d ,%d');
    if (count < 5) return false;
    const [side, tx, ty, wf, nHealth] = values as number[];
    const fxHealth = s16(idiv(this.m_puntc.GetArmorStrength() * nHealth, 100));
    return this.Init(
      WcFromTc(tx),
      WcFromTc(ty),
      this.ctx.plrm.GetPlayer(side) as Player,
      fxHealth,
      wf >>> 0,
    );
  }

  // game/Unit.cpp:344-360 (knVerUnitGobState 4)
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 4) return false;
    this.m_fxHealth = (pstm.ReadWord() << 16) >> 16; // (fix)ReadWord() — fix is SIGNED (Unit.cpp:348)
    this.m_wfUnit = pstm.ReadWord();
    for (let i = 0; i < this.m_agidEnemyNearby.length; i++) {
      this.m_agidEnemyNearby[i] = pstm.ReadWord();
    }
    this.m_ani.Init(this.ctx.animDataFor(this.m_puntc.szAniName));
    this.m_ani.LoadState(pstm);
    this.m_cupdLastHitNotify = pstm.ReadDword() | 0;
    this.m_cDamageCountdown = pstm.ReadWord();
    return super.LoadState(pstm);
  }

  /** True when the C++ class's Draw() mutates m_ani (SetStrip/SetFrame as a render
   *  trick), making the SAVED strip/frame client-viewport presentation state the
   *  headless TS can't reproduce — ReplicatorGob (sim-quirks B21). */
  protected aniSaveIsDrawMutated(): boolean {
    return false;
  }

  // game/Unit.cpp:362-374
  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(4);
    pstm.WriteWord(this.m_fxHealth);
    pstm.WriteWord(this.m_wfUnit);
    for (let i = 0; i < this.m_agidEnemyNearby.length; i++) {
      pstm.WriteWord(this.m_agidEnemyNearby[i]);
    }
    this.m_ani.SaveState(pstm);
    if (this.aniSaveIsDrawMutated()) {
      // strip+frame bytes sit 4 back from the ani block end ([ver strip frame
      // cDelay cCountdown wf:2]); masked on BOTH sides (sim-quirks B21)
      markDeadSpan(pstm, 2, { both: true, back: 4 });
    }
    pstm.WriteDword(this.m_cupdLastHitNotify);
    pstm.WriteWord(this.m_cDamageCountdown);
    // The C++ never zero-inits m_cDamageCountdown (no ctor write; plain heap new on
    // SDL/web), and the Unit.cpp:693 decrement is gated off while kfGobBeingBuilt —
    // so a never-hit unit (esp. one still being built) SAVES frozen heap garbage
    // where the TS has its deterministic 0 (sim-quirks B20).
    markDeadSpan(pstm, 2);
    return super.SaveState(pstm);
  }

  // game/Unit.cpp:276-323 — UnitGob::Init(wx, wy, pplr, fxHealth, ff, const char *pszName).
  // The trailing pszName is part of the real C++ signature (the unit-group builder passes it);
  // the body never reads it (as in C++), so it is an optional, body-unused param. Type-only:
  // restores the real arity so the 6-arg call sites type-check.
  Init(
    wx: number,
    wy: number,
    pplr: Player,
    fxHealth: number,
    ff: number,
    _pszName?: string | null,
  ): boolean {
    this.m_pplr = pplr;
    this.m_wx = WcTrunc(wx);
    this.m_wy = WcTrunc(wy);
    this.m_ff = (this.m_ff | ff) >>> 0;

    const panid = this.ctx.animDataFor(this.m_puntc.szAniName);
    this.m_ani.Init(panid);
    this.StartAnimation(this.m_ani, 0, 0, kfAniLoop | kfAniIgnoreFirstAdvance);

    if (fxHealth === 0) this.m_fxHealth = this.m_puntc.GetArmorStrength();
    else this.m_fxHealth = fxHealth;

    // fog reveal for the LOCAL player (per-client, unhashed; fogmap lands at M3)
    if (this.m_pplr === this.ctx.plrLocal) {
      const wpt = { wx: 0, wy: 0 };
      this.GetCenter(wpt); // Unit.cpp:305 reveals at the CENTER tile
      this.ctx.host.RevealFog(
        this,
        (this.m_puntc.wf & kfUntcLargeDefog) !== 0,
        TcFromWc(wpt.wx),
        TcFromWc(wpt.wy),
      );
    }

    this.ctx.gobm.AddGob(this);

    // Unit.cpp:313-316 — redraw this part of minimap (host content recompute)
    {
      const trc = { left: 0, top: 0, right: 0, bottom: 0 };
      this.GetTileRect(trc);
      this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);
    }

    this.ctx.smm!.SendMsgFrom(kmidReservedEnter, this.m_gid, this.m_gid);
    return true;
  }

  // game/Unit.cpp:325-341 — RemoveGob + delete this (GC)
  Delete(): void {
    // Unit.cpp:327-330 — get trect for minimap redrawing (before removal)
    const trc = { left: 0, top: 0, right: 0, bottom: 0 };
    this.GetTileRect(trc);

    this.ctx.gobm.RemoveGob(this);

    // Unit.cpp:336-339 — redraw this part of minimap after the gob is gone
    this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);
  }

  // game/Unit.cpp:377-383
  IsAlly(side: number): boolean {
    return (this.m_pplr.GetAllies() & GetSideMask(side)) !== 0;
  }

  // game/Unit.cpp:385-393
  Activate(): void {
    this.m_ff = (this.m_ff | kfGobActive) >>> 0;
    (this.m_pplr as Player).IncUnitCount(this.m_puntc.ut);
    (this.m_pplr as Player).IncUnitBuiltCount(this.m_puntc.ut);
  }

  // game/Unit.cpp:395-415 — the menu-close + hilight-sprite arms are host
  Deactivate(): void {
    this.ctx.host.CloseUnitMenu(this);
    this.m_ff = (this.m_ff & ~(kfGobActive | kfGobDrawFlashed)) >>> 0;
    this.Select(false);
    (this.m_pplr as Player).DecUnitCount(this.m_puntc.ut);
    this.ClearDamageIndicator();
  }

  IsIdle(): boolean {
    return true;
  } // Unit.cpp:417-420

  // game/Unit.cpp:422-426
  IsValidTarget(pgobTarget: Gob): boolean {
    return (
      (pgobTarget.GetFlags() & (kfGobUnit | kfGobActive)) === (kfGobUnit | kfGobActive) &&
      !this.IsAlly(pgobTarget.GetSide())
    );
  }

  // game/Unit.cpp:428-430 — base no-op
  SetTarget(
    _gid: number,
    _wxT: number,
    _wyT: number,
    _wxC: number,
    _wyC: number,
    _tcRange: number,
    _wcMoveDist: number,
  ): void {}

  // game/Unit.cpp:432-449 — class-based damage + the Easy-handicap +20% with +50 rounding
  GetDamageTo(puntTarget: UnitGob): number {
    const umTarget = puntTarget.GetConsts().um;
    let nDamage = 0;
    if (umTarget & kumInfantry) nDamage = this.m_puntc.GetInfantryDamage();
    else if (umTarget & kumVehicles) nDamage = this.m_puntc.GetVehicleDamage();
    else if (umTarget & kumStructures) nDamage = this.m_puntc.GetStructureDamage();

    if ((this.m_pplr as Player).GetHandicap() & kfHcapIncreasedFirePower) {
      nDamage = idiv(nDamage * (100 + knIncreasedFirePowerPercent) + 50, 100);
    }
    return nDamage;
  }

  // game/Unit.cpp:579-589 — center derives from the UI bounds
  override GetCenter(pwpt: WPoint): void {
    const b = this.m_puntc.wrcUIBounds;
    pwpt.wx = s16(this.m_wx + b.left + idiv(b.right - b.left, 2));
    pwpt.wy = s16(this.m_wy + b.top + idiv(b.bottom - b.top, 2));
  }
  GetAttackPoint(pwpt: WPoint): void {
    this.GetCenter(pwpt);
  }

  // game/Unit.cpp:607-633 — selection state (persisted m_ff bits)
  Select(fSelect: boolean): void {
    if (fSelect) {
      if (this.m_ff & kfGobSelected) return;
      this.m_ff = (this.m_ff | kfGobSelected | kfGobLayerSelection) >>> 0;
    } else {
      if (!(this.m_ff & kfGobSelected)) return;
      this.m_ff = (this.m_ff & ~kfGobSelected) >>> 0;
      if ((this.m_wfUnit & kfUnitHilighted) === 0) {
        this.m_ff = (this.m_ff & ~kfGobLayerSelection) >>> 0;
      }
    }
    this.MarkRedraw();

    // Unit.cpp:627-631 — redraw this part of minimap
    const trc = { left: 0, top: 0, right: 0, bottom: 0 };
    this.GetTileRect(trc);
    this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);
  }

  // game/Unit.cpp:664-674
  SetHealth(fxHealth: number): void {
    if (this.m_wfUnit & kfUnitInvulnerable) return;
    if (this.m_fxHealth !== fxHealth) {
      this.m_fxHealth = fxHealth;
      if (
        this.m_ff & (kfGobBeingBuilt | kfGobSelected) ||
        this.m_wfUnit & kfUnitDrawHealthIndicator
      ) {
        this.MarkRedraw();
      }
    }
  }

  // game/Unit.cpp:676-706 — flashing + damage-indicator countdown (gevm flags are host)
  DefUpdate(): void {
    if (this.m_ff & kfGobFlashing) {
      this.m_ff = ((this.m_ff & ~kfGobFlashing) | kfGobDrawFlashed | kfGobRedraw) >>> 0;
      this.m_unvl.MinSkip();
      this.ctx.host.SetRedrawBeforeTimer();
    } else if (this.m_ff & kfGobDrawFlashed) {
      this.m_ff = ((this.m_ff & ~kfGobDrawFlashed) | kfGobRedraw) >>> 0;
      this.m_fAttackMoveFlash = false; // the flash frame drew — back to the white default
      this.m_unvl.MinSkip();
    }

    if (this.m_cDamageCountdown !== 0 && !(this.m_ff & kfGobBeingBuilt)) {
      this.m_cDamageCountdown = s16(this.m_cDamageCountdown - this.m_unvl.GetUpdateCount());
      if (this.m_cDamageCountdown <= 0) {
        this.m_cDamageCountdown = 0;
        this.ClearDamageIndicator();
      } else {
        this.m_unvl.MinSkip(this.m_cDamageCountdown);
      }
    }
  }

  // game/Unit.cpp:750-767 — rectangular fallback past 10, Euclidean table inside
  CalcRange(tx: number, ty: number, pgob: Gob): number {
    const tpt: TPoint = { tx: 0, ty: 0 };
    pgob.GetTilePosition(tpt);
    const tcx = Math.abs(tx - tpt.tx);
    const tcy = Math.abs(ty - tpt.ty);
    if (tcx >= 10 || tcy >= 10) return tcx > tcy ? tcx : tcy;
    return gmpDistFromDxy[tcx][tcy];
  }

  // game/Unit.cpp:769-808 — bounded remember-list; replace dead/same-side or farther entries
  RememberEnemyNearby(gidEnemy: number): void {
    for (let n = 0; n < kcEnemyNearbyRemember; n++) {
      if (this.m_agidEnemyNearby[n] === gidEnemy) return;
    }
    const puntEnemy = this.ctx.gobm.GetGob(gidEnemy);
    if (puntEnemy === null) return;
    const txThis = TcFromWc(this.m_wx);
    const tyThis = TcFromWc(this.m_wy);
    const sideThis = this.GetSide();
    const tcEnemy = this.CalcRange(txThis, tyThis, puntEnemy);
    for (let n = 0; n < kcEnemyNearbyRemember; n++) {
      const puntNearby = this.ctx.gobm.GetGob(this.m_agidEnemyNearby[n]);
      if (puntNearby === null || puntNearby.GetSide() === sideThis) {
        this.m_agidEnemyNearby[n] = gidEnemy;
        this.m_ff = (this.m_ff & ~kfGobNoEnemiesNearby) >>> 0;
        return;
      }
      const tc = this.CalcRange(txThis, tyThis, puntNearby);
      if (tc > tcEnemy) {
        this.m_agidEnemyNearby[n] = gidEnemy;
        this.m_ff = (this.m_ff & ~kfGobNoEnemiesNearby) >>> 0;
        return;
      }
    }
  }

  // game/Unit.cpp:810-855 — closest live enemy within range; full-empty sets the no-scan bit
  FindEnemyNearby(tcRange: number): UnitGob | null {
    if (this.m_ff & kfGobNoEnemiesNearby) return null;
    const txThis = TcFromWc(this.m_wx);
    const tyThis = TcFromWc(this.m_wy);
    const sideThis = this.GetSide();

    let cEmpty = 0;
    let tcClosest = tcRange;
    let puntClosest: UnitGob | null = null;
    for (let n = 0; n < kcEnemyNearbyRemember; n++) {
      const gid = this.m_agidEnemyNearby[n];
      if (gid === kgidNull) {
        cEmpty++;
        continue;
      }
      const punt = this.ctx.gobm.GetGob(gid) as UnitGob | null;
      if (punt === null || punt.GetSide() === sideThis) {
        this.m_agidEnemyNearby[n] = kgidNull;
        cEmpty++;
        continue;
      }
      const tc = this.CalcRange(txThis, tyThis, punt);
      if (tc <= tcClosest) {
        tcClosest = tc;
        puntClosest = punt;
      }
    }
    if (puntClosest !== null) return puntClosest;
    if (cEmpty === kcEnemyNearbyRemember) this.m_ff = (this.m_ff | kfGobNoEnemiesNearby) >>> 0;
    return null;
  }

  // game/Unit.cpp:858-958 — the sight sweep: DISCOVERY side-effects (SDIS hashed) + mutual
  // kmidEnemyNearby notifications. Tile scan order: y rows, x cols, gid chains.
  NotifyEnemyNearby(): void {
    const { ctx: mapCtx, cty } = this.ctx.gobm.GetMapSize();
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);

    let txLeft = tx - ktcSightRangeMax;
    if (txLeft < 0) txLeft = 0;
    let txRight = tx + ktcSightRangeMax + 1;
    if (txRight > mapCtx) txRight = mapCtx;
    let tyTop = ty - ktcSightRangeMax;
    if (tyTop < 0) tyTop = 0;
    let tyBottom = ty + ktcSightRangeMax + 1;
    if (tyBottom > cty) tyBottom = cty;

    let sidmAlreadyDiscovered = (this.m_pplr as Player).GetDiscoveredSides();

    for (let tyT = tyTop; tyT < tyBottom; tyT++) {
      for (let txT = txLeft; txT < txRight; txT++) {
        const side = this.m_pplr.GetSide();
        for (
          let gid = this.ctx.gobm.GetFirstGid(txT, tyT);
          gid !== kgidNull;
          gid = this.ctx.gobm.GetNextGid(gid)
        ) {
          const punt = this.ctx.gobm.GetGob(gid) as UnitGob | null;
          if (punt === null) continue;
          const ffUnit = punt.GetFlags();
          if (!(ffUnit & kfGobUnit)) continue;

          const sidm = GetSideMask(punt.GetSide());
          if (!(sidmAlreadyDiscovered & sidm)) {
            sidmAlreadyDiscovered |= sidm;
            (this.m_pplr as Player).SetDiscoveredSides(sidmAlreadyDiscovered);
            const tpt: TPoint = { tx: 0, ty: 0 };
            punt.GetTilePosition(tpt);
            (this.m_pplr as Player).SetDiscoverPoint(tpt);

            const pplrB = punt.m_pplr as Player;
            pplrB.SetDiscoveredSides(pplrB.GetDiscoveredSides() | GetSideMask(side));
            pplrB.SetDiscoverPoint(tpt);
          }

          if (punt.IsAlly(side)) continue;

          const gidFound = punt.GetId();
          if (this.ctx.unitConsts[punt.GetUnitType()].wf & kfUntcNotifyEnemyNearby) {
            const msg = newMessage(kmidEnemyNearby, this.m_gid, gidFound);
            msg.EnemyNearby.gidEnemy = this.m_gid;
            this.ctx.smm!.SendMsg(msg);
          }
          // DIVERGENCE v3 (issue: attack-move ignores towers): the C++ only let a unit self-remember
          // MOBILE enemies from its sweep, so an enemy TOWER (a firing structure — never mobile)
          // never entered m_agidEnemyNearby and FindEnemyNearby never returned it. A unit
          // attack-moving (or guarding) past a tower would pass it by, only ever eating its fire.
          // From simVersion 3 we also remember enemy towers — any firing structure. Version-gated so
          // pre-v3 recordings replay with their original behavior (tasks/sim-quirks.md D1).
          const puntcFound = this.ctx.unitConsts[punt.GetUnitType()];
          if (
            ffUnit & kfGobMobileUnit ||
            (this.ctx.simVersion >= 3 && puntcFound.tcFiringRange > 0)
          ) {
            if (this.m_puntc.wf & kfUntcNotifyEnemyNearby) {
              const msg = newMessage(kmidEnemyNearby, this.m_gid, this.m_gid);
              msg.EnemyNearby.gidEnemy = gidFound;
              this.ctx.smm!.SendMsg(msg);
            }
          }
        }
      }
    }
  }

  // game/Unit.cpp:960-973
  RecalcEnemyNearby(fClearOnly: boolean): void {
    if (fClearOnly) {
      this.m_ff = (this.m_ff & ~kfGobNoEnemiesNearby) >>> 0;
      this.m_agidEnemyNearby.fill(kgidNull);
      return;
    }
    this.NotifyEnemyNearby();
  }

  // game/Unit.cpp:983-1046 — once-per-2-updates throttle, 4-tile ally sweep
  NotifyNearbyAlliesOfHit(gidAssailant: number): void {
    const cupd = this.ctx.sim!.GetUpdateCount();
    if (cupd - this.m_cupdLastHitNotify < 2) return;
    this.m_cupdLastHitNotify = cupd;

    const { ctx: mapCtx, cty } = this.ctx.gobm.GetMapSize();
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    let txLeft = tx - ktcNotifyAlliesOfHitRange;
    if (txLeft < 0) txLeft = 0;
    let txRight = tx + ktcNotifyAlliesOfHitRange + 1;
    if (txRight > mapCtx) txRight = mapCtx;
    let tyTop = ty - ktcNotifyAlliesOfHitRange;
    if (tyTop < 0) tyTop = 0;
    let tyBottom = ty + ktcNotifyAlliesOfHitRange + 1;
    if (tyBottom > cty) tyBottom = cty;

    for (let tyT = tyTop; tyT < tyBottom; tyT++) {
      for (let txT = txLeft; txT < txRight; txT++) {
        const side = this.m_pplr.GetSide();
        for (
          let gid = this.ctx.gobm.GetFirstGid(txT, tyT);
          gid !== kgidNull;
          gid = this.ctx.gobm.GetNextGid(gid)
        ) {
          if (gid === this.m_gid) continue;
          const punt = this.ctx.gobm.GetGob(gid) as UnitGob | null;
          if (punt === null) continue;
          if (!(punt.GetFlags() & kfGobMobileUnit)) continue;
          if (!punt.IsAlly(side)) continue;

          const msg = newMessage(kmidNearbyAllyHit, this.m_gid, punt.GetId());
          msg.Hit.gidAssailant = gidAssailant;
          this.ctx.smm!.SendMsg(msg);
        }
      }
    }
  }

  // game/Unit.cpp:1049-1070 — enemy-health flash; perf flag is a PINNED sim constant
  ShowDamageIndicator(): void {
    if (this.m_pplr === this.ctx.plrLocal) return;
    if (!(this.ctx.wfPerfOptions & kfPerfEnemyDamageIndicator)) return;
    this.m_wfUnit = (this.m_wfUnit | kfUnitDrawHealthIndicator) & 0xffff;
    this.m_ff = (this.m_ff | kfGobLayerSelection) >>> 0;
    this.MarkRedraw();
    this.m_cDamageCountdown = s16(UpdFromMsec(kcmsDamageIndicatorTimeout));
    this.m_unvl.MinSkip(this.m_cDamageCountdown);
  }

  // game/Unit.cpp:1071-1078
  ClearDamageIndicator(): void {
    if ((this.m_ff & kfGobSelected) === 0) {
      this.m_ff = (this.m_ff & ~kfGobLayerSelection) >>> 0;
    }
    this.m_wfUnit = this.m_wfUnit & ~kfUnitDrawHealthIndicator & 0xffff;
    this.MarkRedraw();
  }
}

// game/GameObjects.cpp:470-520 — Gob range tests (live here with the dist table)
export function IsTargetWithinRange(pwptTarget: WPoint, pgobTarget: Gob, tcRange: number): boolean {
  const tx = TcFromWc(pwptTarget.wx);
  const ty = TcFromWc(pwptTarget.wy);

  if (pgobTarget.GetFlags() & kfGobStructure) {
    const trcTarget = new TRect();
    pgobTarget.GetTileRect(trcTarget);
    for (let tyTarget = trcTarget.top; tyTarget < trcTarget.bottom; tyTarget++) {
      for (let txTarget = trcTarget.left; txTarget < trcTarget.right; txTarget++) {
        const dtx = Math.abs(tx - txTarget);
        const dty = Math.abs(ty - tyTarget);
        if (dtx >= 10 || dty >= 10) continue;
        if (gmpDistFromDxy[dtx][dty] <= tcRange) return true;
      }
    }
    return false;
  }
  const tpt: TPoint = { tx: 0, ty: 0 };
  pgobTarget.GetTilePosition(tpt);
  const dtx = Math.abs(tx - tpt.tx);
  const dty = Math.abs(ty - tpt.ty);
  if (dtx >= 10 || dty >= 10) return false;
  return gmpDistFromDxy[dtx][dty] <= tcRange;
}

export function IsGobWithinRange(self: Gob, pgobTarget: Gob, tcRange: number): boolean {
  const wpt: WPoint = { wx: self.m_wx, wy: self.m_wy };
  return IsTargetWithinRange(wpt, pgobTarget, tcRange);
}
